export const recordWidgetAudio = async ({ onComplete, onError, signal }) => {
  if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) throw new Error('Voice recording is not supported in this browser.');
  const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
  if (signal?.aborted) { stream.getTracks().forEach((track) => track.stop()); return null; }
  let recorder;
  let context;
  let timer;
  let limit;
  let cancelled = false;
  let finished = false;
  let cancel;
  const chunks = [];
  const release = () => {
    clearTimeout(timer);
    clearTimeout(limit);
    stream.getTracks().forEach((track) => track.stop());
    if (context) context.close().catch(() => {});
    signal?.removeEventListener('abort', cancel);
  };
  cancel = () => {
    cancelled = true;
    if (recorder?.state === 'recording') recorder.stop();
    release();
  };
  try {
    const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((type) => window.MediaRecorder.isTypeSupported?.(type));
    recorder = new window.MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorder.ondataavailable = (event) => { if (event.data?.size) chunks.push(event.data); };
    recorder.onstop = () => {
      if (finished) return;
      finished = true;
      release();
      if (!cancelled) onComplete(new Blob(chunks, { type: recorder.mimeType || mimeType || chunks[0]?.type || 'audio/webm' }));
    };
    recorder.onerror = () => { cancel(); onError(new Error('Recording failed. Please try again.')); };
    signal?.addEventListener('abort', cancel, { once: true });
    recorder.start();
    limit = setTimeout(() => { if (recorder.state === 'recording') recorder.stop(); }, 120000);
    // Wait for the caller to actually start speaking before arming silence-stop.
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        context = new AudioContext();
        context.resume?.().catch(() => {});
        const analyser = context.createAnalyser();
        analyser.fftSize = 2048;
        context.createMediaStreamSource(stream).connect(analyser);
        const samples = new Float32Array(analyser.fftSize);
        let heardSpeech = false;
        let lastSound = Date.now();
        const check = () => {
          if (recorder.state !== 'recording') return;
          analyser.getFloatTimeDomainData(samples);
          const rms = Math.sqrt(samples.reduce((sum, sample) => sum + sample * sample, 0) / samples.length);
          if (rms > 0.01) { heardSpeech = true; lastSound = Date.now(); }
          if (heardSpeech && Date.now() - lastSound >= 1800) { recorder.stop(); return; }
          timer = setTimeout(check, 100);
        };
        timer = setTimeout(check, 100);
      }
    } catch (_) { /* Manual stop and the duration limit still work without VAD. */ }
    return { stop: () => { if (recorder.state === 'recording') recorder.stop(); }, cancel };
  } catch (error) { release(); throw error; }
};
